import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const paymentId = params.get("paymentId");
    const payerId = params.get("PayerID");

    const paypal = async () => {
      try {
        const { data } = await api.post(`/payment/execute?payment_id=${paymentId}&payer_id=${payerId}`);
        if (data.claim_ready) return setState("claim");
        if (data.claim_via_email) return setState("email");
        await refreshUser();
        setState("paid");
      } catch {
        setState("failed");
      }
    };

    if (paymentId && payerId) paypal();
    else setState("failed");
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center mm-gradient-soft px-5">
      <div className="mm-card p-10 max-w-md w-full text-center mm-fade-up" data-testid="payment-success-card">
        {state === "checking" && (<><Loader2 className="animate-spin mx-auto text-mm-primary" size={48} /><h1 className="text-2xl font-bold mt-4">Confirming your payment…</h1></>)}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-mm-success" size={56} />
            <h1 className="text-3xl font-bold mt-4" data-testid="payment-success-title">You're all set! 🎉</h1>
            <p className="text-mm-muted mt-2">Lifetime access unlocked. Welcome to Mindful Me.</p>
            <Link to="/" className="mm-btn mt-6" data-testid="payment-success-continue">Start my journey</Link>
          </>
        )}
        {state === "claim" && (
          <>
            <CheckCircle2 className="mx-auto text-mm-success" size={56} />
            <div className="text-xs font-semibold text-mm-primary uppercase tracking-wider mt-4">Step 2 of 2 · Create your account</div>
            <h1 className="text-3xl font-bold mt-2" data-testid="payment-claim-title">Payment received 🎉</h1>
            <p className="text-mm-muted mt-2">Create your account now and it will be unlocked automatically.</p>
            <Link to="/login?mode=register" className="mm-btn mt-6" data-testid="payment-create-account">Create my account</Link>
          </>
        )}
        {state === "email" && (
          <>
            <Mail className="mx-auto text-mm-primary" size={56} />
            <h1 className="text-3xl font-bold mt-4" data-testid="payment-email-title">Payment received 🎉</h1>
            <p className="text-mm-muted mt-2" data-testid="payment-email-text">For your security, finish signing up from the link in your receipt email — it works on any device.</p>
            <Link to="/checkout" className="mm-btn-ghost mt-6" data-testid="payment-email-resend">Didn't get it? Resend receipt</Link>
          </>
        )}
        {["paid", "claim", "email"].includes(state) && (
          <p className="text-xs text-mm-muted mt-6" data-testid="payment-refund-note">Full refund within 14 days of purchase. Email <a href="mailto:mindfulmeyourcalmcompanion@gmail.com" className="underline">mindfulmeyourcalmcompanion@gmail.com</a>.</p>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto text-mm-error" size={56} />
            <h1 className="text-2xl font-bold mt-4">Payment not completed</h1>
            <p className="text-mm-muted mt-2">No charge was made. You can try again.</p>
            <Link to="/checkout" className="mm-btn mt-6" data-testid="payment-retry">Back to checkout</Link>
          </>
        )}
      </div>
    </div>
  );
}
